import React, { useRef, useState } from 'react';
import {
  Box,
  FormLabel,
  Stack,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import TitleIcon from '@mui/icons-material/Title';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import { renderRichText } from 'dashboard/components/siteRenderer/richText';

/**
 * A writing field for the long text in a library entry.
 *
 * A blog post was being typed into a single-line input, and whatever came out
 * was rendered as one unbroken run of characters. Nothing could be a heading,
 * emphasised, a list or a link.
 *
 * The buttons write the same notation someone can type by hand, rather than
 * hiding it behind a WYSIWYG surface. That keeps what is stored readable and
 * portable — it is still just text — and it means the preview beside it is
 * the whole truth about what the page will do with it, which a contentEditable
 * built on execCommand could not honestly promise.
 */

const TOOLS = [
  { key: 'bold', icon: FormatBoldIcon, label: 'Bold', wrap: '**' },
  { key: 'italic', icon: FormatItalicIcon, label: 'Italic', wrap: '*' },
  { key: 'heading', icon: TitleIcon, label: 'Heading', prefix: '## ' },
  { key: 'bullet', icon: FormatListBulletedIcon, label: 'Bullet list', prefix: '- ' },
  { key: 'numbered', icon: FormatListNumberedIcon, label: 'Numbered list', prefix: '1. ' },
  { key: 'quote', icon: FormatQuoteIcon, label: 'Quote', prefix: '> ' },
  { key: 'link', icon: LinkIcon, label: 'Link', link: true },
];

export default function RichTextField({
  id, label, value = '', onChange, placeholder, mb, minRows = 6,
}) {
  const ref = useRef(null);
  const [tab, setTab] = useState('write');

  /**
   * Apply a tool to the selection.
   *
   * Selection is read from the textarea rather than tracked in state: the
   * browser already knows where the caret is, and a second copy of that would
   * be wrong every time focus moved.
   *
   * The edit goes through `execCommand('insertText')` where the browser has
   * it. That reads as a detour — we have the string and we have `onChange` —
   * but setting the value through React replaces the textarea's contents
   * wholesale, and the browser drops its undo history when that happens.
   * Ctrl+Z did nothing to anything a button had done. `insertText` is
   * recorded as an ordinary typed edit, so undo and redo work across the
   * buttons and the keyboard alike, and it raises an `input` event, so
   * `onChange` still runs and nothing else has to change.
   */
  const apply = (tool) => {
    const el = ref.current?.querySelector('textarea');
    if (!el) return;

    // The textarea may never have been focused, in which case both offsets
    // read 0 and every button wrote to the very start of the post. That is
    // what put a run of stray markers at the top of the box. The buttons also
    // refuse to take focus (see onMouseDown), so by this point the caret is
    // still wherever the writer left it.
    el.focus();
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? start;
    const selected = value.slice(start, end);

    // Every tool is a replacement of one range, and what differs is the range
    // and the string. Working that out first means one write below, rather
    // than three that each have to get undo and the caret right.
    let from = start;
    const to = end;
    let insert;
    // Where the caret lands, relative to the start of what was inserted.
    let selFrom;
    let selTo;

    if (tool.wrap) {
      const body = selected || tool.label.toLowerCase();
      insert = tool.wrap + body + tool.wrap;
      // Nothing was selected, so the placeholder is — type and it is gone.
      // It used to be left behind as a literal word, which is how "italic"
      // ended up written into people's posts.
      selFrom = selected ? insert.length : tool.wrap.length;
      selTo = selected ? insert.length : tool.wrap.length + body.length;
    } else if (tool.link) {
      const body = selected || 'link text';
      insert = `[${body}](/)`;
      // With no selection the words are offered first; with one, the caret
      // goes between the brackets of the address, which is the part still
      // missing either way.
      selFrom = selected ? body.length + 3 : 1;
      selTo = selected ? body.length + 3 : 1 + body.length;
    } else {
      // Line prefixes attach to the start of every line in the selection, so
      // selecting four lines and pressing bullets makes four bullets.
      from = value.lastIndexOf('\n', start - 1) + 1;
      const chunk = value.slice(from, end) || '';
      insert = (chunk || '').split('\n').map((l) => tool.prefix + l).join('\n');
      selFrom = insert.length;
      selTo = insert.length;
    }

    el.setSelectionRange(from, to);

    let wrote = false;
    try {
      // Returns false where the command is unsupported, and throws in a few
      // older engines rather than returning anything at all.
      wrote = document.execCommand('insertText', false, insert);
    } catch (_) {
      wrote = false;
    }

    if (!wrote) {
      // No undo here, but the edit still has to happen.
      onChange({ target: { value: value.slice(0, from) + insert + value.slice(to) } });
    }

    // After the value has settled, put the caret where the writer expects it
    // — otherwise it sits at the end of whatever was just inserted.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(from + selFrom, from + selTo);
    });
  };

  return (
    <Stack direction='column' sx={{ mb: mb || '30px' }}>
      {label && (
        <FormLabel
          htmlFor={id}
          sx={{
            display: 'flex', ml: '10px', fontSize: '0.875rem',
            color: 'text.primary', fontWeight: 700, cursor: 'pointer', mb: '6px',
            textTransform: 'capitalize',
          }}
        >
          {label}
        </FormLabel>
      )}

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap='8px'
        flexWrap='wrap'
        mb='6px'
      >
        <Stack direction='row' gap='2px' flexWrap='wrap'>
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <Tooltip key={t.key} title={t.label}>
                <Box
                  component='button'
                  type='button'
                  aria-label={t.label}
                  // Without this the button takes focus on press, the
                  // textarea loses its selection, and the tool applies to an
                  // empty range at the top of the field.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => apply(t)}
                  sx={{
                    border: '1px solid', borderColor: 'divider', background: 'none',
                    borderRadius: '8px', p: '5px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', color: 'text.secondary',
                    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                  }}
                >
                  <Icon sx={{ fontSize: 17 }} />
                </Box>
              </Tooltip>
            );
          })}
        </Stack>

        <ToggleButtonGroup
          size='small'
          exclusive
          value={tab}
          onChange={(_e, v) => v && setTab(v)}
        >
          <ToggleButton value='write' sx={{ py: '2px', px: '10px', fontSize: 12 }}>Write</ToggleButton>
          <ToggleButton value='preview' sx={{ py: '2px', px: '10px', fontSize: 12 }}>Preview</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {tab === 'write' ? (
        <MuiTextField
          ref={ref}
          id={id}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          multiline
          minRows={minRows}
          size='small'
        />
      ) : (
        <Box
          sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: '8px',
            p: '14px', minHeight: minRows * 24,
            // The preview runs the renderer the public page runs, so what is
            // shown here is not an approximation of the result.
            '& p:last-child, & ul:last-child, & ol:last-child': { mb: 0 },
          }}
        >
          {renderRichText(value, { scale: 15 }) || (
            <Typography variant='body2' color='text.secondary' fontStyle='italic'>
              Nothing to preview yet.
            </Typography>
          )}
        </Box>
      )}

      <Typography variant='caption' color='text.secondary' mt='4px' ml='10px'>
        **bold**, *italic*, # heading, - bullet, &gt; quote, [words](/page)
      </Typography>
    </Stack>
  );
}
